import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useSnackbar } from 'notistack'
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    TextField,
} from '@mui/material'
import Tab from '@mui/material/Tab'
import TabList from '@mui/lab/TabList'
import { TabContext, TabPanel } from '@mui/lab'
import { cancelTransaction, getTransactionById, selectItemById } from 'app/redux/store/features/transactions'
import utilServices from 'app/services/util-services'
import HeaderCard from './components/headerCard/HeaderCard'
import InfoBar from './components/infoBar/InfoBar'
import TxnItemsGrid from './components/txnItems/TxnItemsGrid'
import TxnPaymentGrid from './components/txnPayment/TxnPaymentGrid'

const TxnView = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const { state } = useLocation()
    const { id } = state
    const [apiCallInProgress, setApiCallInProgress] = useState(false)
    const [cancelRequested, setCancelRequested] = useState(false)
    const { transactions, loading, error } = useSelector((reduxState) => reduxState.transactions)
    const transaction = selectItemById(transactions, id)

    const [tabTitle, setTabTitle] = React.useState('items')
    const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
    const [cancellationReason, setCancellationReason] = useState('')

    const isCancelled = transaction?.txnStatusName?.toLowerCase() === 'cancelled'

    useEffect(() => {
        if (utilServices.isNullOrUndefined(transaction.id)) dispatch(getTransactionById({ id }))
    }, [transaction.txnPayments])

    useEffect(() => {
        if (!apiCallInProgress || loading) return
        setApiCallInProgress(false)

        if (error) {
            handleError(error)
            setCancelRequested(false)
        } else if (cancelRequested && transaction?.txnStatusName?.toLowerCase() === 'cancelled') {
            enqueueSnackbar('Transaction cancelled successfully', {
                variant: 'success',
                anchorOrigin: { vertical: 'top', horizontal: 'right' },
            })
            setCancellationReason('')
            setCancelRequested(false)
        }
    }, [loading])

    function handleError(err) {
        if (err.status === '401') {
            navigate('/profile/signout')
        } else if (err.detail) {
            enqueueSnackbar(err.detail, {
                variant: 'error',
                anchorOrigin: { vertical: 'top', horizontal: 'right' },
            })
        } else {
            enqueueSnackbar(err, {
                variant: 'error',
                anchorOrigin: { vertical: 'top', horizontal: 'right' },
            })
        }
    }

    function handleCancelConfirm() {
        setApiCallInProgress(true)
        setCancelRequested(true)
        setCancelDialogOpen(false)
        dispatch(
            cancelTransaction(id, {
                cancellationReason: cancellationReason.trim() || undefined,
                cancelEnrolments: true,
            })
        )
    }

    return (
        <Grid container spacing={3.75}>
            <Grid item xs={12} sm={12} lg={12}>
                <HeaderCard transaction={transaction} />
            </Grid>
            <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
                <DialogTitle>Cancel Transaction</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        This will refund any Stripe payment, cancel the transaction, and remove linked enrolments. This
                        action cannot be undone.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        fullWidth
                        label='Reason (optional)'
                        value={cancellationReason}
                        onChange={(e) => setCancellationReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancelDialogOpen(false)} sx={{ textTransform: 'none' }}>
                        Back
                    </Button>
                    <Button
                        onClick={handleCancelConfirm}
                        color='warning'
                        variant='contained'
                        sx={{ textTransform: 'none', fontWeight: 600 }}>
                        Confirm Cancel
                    </Button>
                </DialogActions>
            </Dialog>
            <Grid item xs={12} sm={6} lg={12}>
                <InfoBar
                    transaction={transaction}
                    isCancelled={isCancelled}
                    onCancelClick={() => setCancelDialogOpen(true)}
                />
            </Grid>
            <Grid item xs={12} sm={6} lg={12}>
                <Grid sx={{ mt: -2 }}>
                    <TabContext value={tabTitle}>
                        <TabList sx={{ bgcolor: 'divider' }} onChange={(e, newValue) => setTabTitle(newValue)}>
                            <Tab label={'Items'} value={'items'} sx={{ width: '160px' }} />
                            <Tab label={'Payments'} value={'payments'} sx={{ width: '160px' }} />
                        </TabList>
                        <TabPanel
                            value='items'
                            sx={{
                                pt: '0%',
                                pl: '0%',
                                pr: '0%',
                            }}>
                            <TxnItemsGrid id={id} />
                        </TabPanel>
                        <TabPanel
                            value='payments'
                            sx={{
                                pl: '0%',
                                pr: '0%',
                                pt: '0%',
                            }}>
                            <TxnPaymentGrid id={id} />
                        </TabPanel>
                    </TabContext>
                </Grid>
            </Grid>
        </Grid>
    )
}

export default TxnView
